/**
 * Carga menú público desde API externa o snapshots estáticos cuando la API principal falla.
 * Normalización: @/lib/menuPublicSnapshot (contrato MenuPublicSnapshot).
 */

import {
  toMenuPublicSnapshot,
  toLiveMenuPublicSnapshot,
} from '@/lib/menuPublicSnapshot';
import {
  MENU_PUBLIC_API_BASE,
  buildMenuPublicApiUrl,
  staticMenuSnapshotUrls,
} from '@/lib/menuFallbackChain';

export {
  toMenuPublicSnapshot as normalizePublicMenuPayload,
  toLiveMenuPublicSnapshot as normalizeLiveMenuPayload,
} from '@/lib/menuPublicSnapshot';

export {
  MENU_FALLBACK_SOURCES,
  MENU_PUBLIC_API_BASE,
  detectMenuFallbackTier,
  buildMenuPublicApiUrl,
  staticMenuSnapshotUrls,
} from '@/lib/menuFallbackChain';

/** Request config: sin auth y sin cache del navegador para menú público. */
export const MENU_API_NO_CACHE = {
  skipAuth: true,
  headers: {
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  },
};

export function menuApiPath(businessId) {
  const id = String(businessId || '').trim();
  return `/menu/${encodeURIComponent(id)}?_=${Date.now()}`;
}

export async function fetchExternalMenuFallback(slug) {
  const safeSlug = String(slug || '').trim().toLowerCase();
  if (!safeSlug) return null;

  try {
    const url = buildMenuPublicApiUrl(safeSlug);
    if (!url) return null;
    const response = await fetch(url, {
      cache: 'no-cache',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    const data = await response.json();
    return toMenuPublicSnapshot(
      {
        ...data,
        fallback_mode: false,
        fallback_source: 'menu_public_api',
        fallback_checkout_disabled: false,
      },
      safeSlug,
    );
  } catch {
    return null;
  }
}

export async function fetchMenuFallbackSnapshot(slug) {
  const safeSlug = String(slug || '').trim().toLowerCase();
  if (!safeSlug) return null;

  for (const url of staticMenuSnapshotUrls(safeSlug)) {
    try {
      const response = await fetch(url, {
        cache: 'no-cache',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) continue;
      const data = await response.json();
      return toMenuPublicSnapshot(
        {
          ...data,
          fallback_mode: true,
          fallback_source: url,
          fallback_checkout_disabled: true,
        },
        safeSlug,
      );
    } catch {
      continue;
    }
  }

  return null;
}

/**
 * Carga menú público con la misma cadena que catálogo/bienvenida (API → external → snapshot).
 * Usar en welcome, catálogo, asistente y checkout para evitar drift.
 */
export async function loadPublicMenuPayload(api, businessId, options = {}) {
  const id = String(businessId || '').trim();
  const isDemo = Boolean(options.isDemo);
  const getDemo = options.getDemoPayload;

  if (isDemo && typeof getDemo === 'function') {
    return {
      payload: getDemo(),
      loadError: false,
      toastMessage: 'Vista previa lista',
      fromFallback: false,
    };
  }

  if (!id) {
    return { payload: null, loadError: true, fromFallback: false };
  }

  try {
    const response = await api.get(menuApiPath(id), MENU_API_NO_CACHE);
    const normalized = toLiveMenuPublicSnapshot(response.data || {}, id);
    const liveProducts = Array.isArray(normalized.products) ? normalized.products : [];

    if (liveProducts.length === 0) {
      const external = await fetchExternalMenuFallback(id);
      const externalProducts = Array.isArray(external?.products) ? external.products : [];
      if (external && externalProducts.length > 0) {
        return {
          payload: external,
          loadError: false,
          fromFallback: true,
          toastMessage: 'Menú disponible',
        };
      }
    }

    return { payload: normalized, loadError: false, fromFallback: false };
  } catch (error) {
    if (isDemo && typeof getDemo === 'function') {
      return {
        payload: getDemo(),
        loadError: false,
        fromFallback: false,
        toastMessage: 'Vista previa lista',
      };
    }

    let fallback = await fetchExternalMenuFallback(id);
    if (!fallback) {
      fallback = await fetchMenuFallbackSnapshot(id);
    }
    if (fallback) {
      return {
        payload: fallback,
        loadError: false,
        fromFallback: true,
        status: error?.response?.status,
        toastMessage:
          fallback.fallback_source === 'menu_public_api'
            ? 'Menú disponible'
            : 'Menú disponible en modo emergencia',
      };
    }

    return {
      payload: null,
      loadError: true,
      fromFallback: false,
      status: error?.response?.status,
      toastMessage:
        error?.response?.status === 403
          ? 'Este negocio no está disponible actualmente'
          : 'Error al cargar el menú',
    };
  }
}
